/**
 * useOperationMountReconcile.js — Phase -1 Tier 1 — S5.
 *
 * A single, ordered mount-pass hook that replaces the per-section
 * E1/E2/E3/E4 effects with one backend-authoritative reconcile.
 *
 * Contract:
 *
 *   const reconcile = useOperationMountReconcile({
 *     targetKind, targetId, actionCode,
 *     enabled = true,
 *     onTerminal,     // (operationRow) => void  — found terminal op
 *     onRecovering,   // (operationRow) => void  — found active op
 *     onNone,         // () => void               — nothing in flight
 *     onError,        // (err) => void            — fetch failed
 *   })
 *
 * Returns:
 *   {
 *     status,        // 'idle' | 'reconciling' | 'recovering' | 'terminal' | 'none' | 'error'
 *     operation,     // the operations row, if found
 *     refetch,       // manually re-reconcile (e.g. after dispatch)
 *   }
 *
 * Behaviour:
 *   1. On mount + target/action change: GET /api/operations/me/active
 *      and find the most recent row matching (target_kind, target_id,
 *      action_code).
 *   2. If status is terminal → onTerminal(row); status='terminal'.
 *   3. If status is active   → onRecovering(row); status='recovering'.
 *   4. If no match            → onNone(); status='none'.
 *   5. Subscribes to `visibilitychange` (visible) + `focus` events,
 *      debounced 200 ms, so tab-return triggers a single re-reconcile.
 *   6. Backend errors are logged and surfaced via onError; the hook
 *      does NOT crash the section.
 *
 * Strict rails:
 *   • Read-only. Never dispatches. Never charges.
 *   • Sections that want to *attach polling* on the recovering row
 *     do so themselves via `controller.attachExistingRun(...)`.
 *   • If the `/me/active` endpoint returns empty (e.g. operations
 *     table not yet migrated), the hook reports status='none' and
 *     the section falls back to its existing LS-marker hint path.
 *
 * Reference: PHASE_MINUS_1_OPERATION_RUNTIME_FOUNDATION.md §5.1
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const TERMINAL_STATUSES = new Set(['complete', 'failed', 'aborted']);
const ACTIVE_STATUSES = new Set([
  'dispatching',
  'polling',
  'recovering',
  'recoverable_stall',
  'stabilizing',
]);

const DEBOUNCE_MS = 200;

/**
 * Find the most-recent matching op for (targetKind, targetId, actionCode).
 * Searches `active` first, then `recent`. Returns `null` if none.
 */
function _findMatchingOp(snapshot, { targetKind, targetId, actionCode }) {
  if (!snapshot) return null;
  const haystack = [
    ...(Array.isArray(snapshot.active) ? snapshot.active : []),
    ...(Array.isArray(snapshot.recent) ? snapshot.recent : []),
  ];
  let best = null;
  for (const row of haystack) {
    if (
      row.target_kind === targetKind
      && row.target_id === targetId
      && row.action_code === actionCode
    ) {
      // Pick the most recently dispatched match.
      if (!best || (row.dispatched_at || '') > (best.dispatched_at || '')) {
        best = row;
      }
    }
  }
  return best;
}

export function useOperationMountReconcile({
  targetKind,
  targetId,
  actionCode,
  enabled = true,
  apiFetch,                       // ({path, opts}) => Promise<json>
  onTerminal,
  onRecovering,
  onNone,
  onError,
}) {
  const [status, setStatus] = useState('idle');
  const [operation, setOperation] = useState(null);
  const cancelledRef = useRef(false);
  const debounceTimerRef = useRef(null);

  // Stabilise callbacks so the effect doesn't re-run on every render.
  const cbRef = useRef({ onTerminal, onRecovering, onNone, onError });
  cbRef.current = { onTerminal, onRecovering, onNone, onError };

  const fetchAndApply = useCallback(async () => {
    if (!enabled) return;
    if (!targetKind || !targetId || !actionCode) return;
    if (typeof apiFetch !== 'function') {
      // No fetch surface available — degrade silently. Section falls
      // back to its existing LS marker path.
      setStatus('none');
      cbRef.current.onNone?.();
      return;
    }
    setStatus('reconciling');
    try {
      const json = await apiFetch('/api/operations/me/active', { method: 'GET' });
      if (cancelledRef.current) return;
      const match = _findMatchingOp(json, { targetKind, targetId, actionCode });
      if (!match) {
        setOperation(null);
        setStatus('none');
        cbRef.current.onNone?.();
        return;
      }
      setOperation(match);
      if (TERMINAL_STATUSES.has(match.status)) {
        setStatus('terminal');
        cbRef.current.onTerminal?.(match);
      } else if (ACTIVE_STATUSES.has(match.status)) {
        setStatus('recovering');
        cbRef.current.onRecovering?.(match);
      } else {
        // Unknown status — treat as no-op.
        setStatus('none');
        cbRef.current.onNone?.();
      }
    } catch (err) {
      if (cancelledRef.current) return;
      // Backend missing / 503 / network blip — degrade silently.
      // The section continues with its existing LS-marker path.
      setStatus('error');
      cbRef.current.onError?.(err);
    }
  }, [enabled, targetKind, targetId, actionCode, apiFetch]);

  // ── Mount + dep change ────────────────────────────────────────────
  useEffect(() => {
    cancelledRef.current = false;
    fetchAndApply();
    return () => {
      cancelledRef.current = true;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [fetchAndApply]);

  // ── visibility + focus reconcile (debounced) ──────────────────────
  useEffect(() => {
    if (!enabled) return undefined;
    const scheduleRefetch = () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        fetchAndApply();
      }, DEBOUNCE_MS);
    };
    const onVisibility = () => {
      if (typeof document !== 'undefined' && !document.hidden) {
        scheduleRefetch();
      }
    };
    const onFocus = () => scheduleRefetch();
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', onFocus);
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', onFocus);
      }
    };
  }, [enabled, fetchAndApply]);

  const result = useMemo(
    () => ({ status, operation, refetch: fetchAndApply }),
    [status, operation, fetchAndApply],
  );
  return result;
}

// Test-only re-export of the matcher so the node test can pin its
// behaviour without standing up the whole hook in jsdom.
export const _findMatchingOpForTests = _findMatchingOp;
